import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { api, type Supplement, type SupplementScan } from '../lib/api';

type ScheduleDraft = { hour: number; minute: number };

// Mirrors the server-side validation in handlers/supplements.go so the form
// gives early feedback rather than waiting for a 400 round-trip. Keep these
// in sync with the Go constants of the same names.
const MAX_SUPPLEMENT_NAME = 80;
const MAX_SUPPLEMENT_DOSE = 80;
const MAX_SCHEDULES = 6;

// AddSupplementModal — sheet/dialog for creating a new supplement. Supports
// two entry paths:
//   1. Manual: type name + dose + schedules and submit.
//   2. Scan: tap "Scan bottle", pick an image; the server hits Claude vision
//      to extract name + dose + label + summary + tags, pre-fills the form,
//      and returns a scan_id that's passed back on submit to claim the photo.
export function AddSupplementModal({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [dose, setDose] = useState('');
  const [schedules, setSchedules] = useState<ScheduleDraft[]>([{ hour: 9, minute: 0 }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<SupplementScan | null>(null);
  const [scanThumbnail, setScanThumbnail] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const submitRef = useRef(false);
  const scanInputRef = useRef<HTMLInputElement | null>(null);
  // Synchronous mirror of `scanning` — setState lags one render, this lets
  // submit() and the picker click reject re-entry before React has caught up.
  const scanningRef = useRef(false);
  // Fields the user has typed into get the touched flag so a subsequent scan
  // doesn't clobber their input, but a re-scan still updates fields they
  // never touched (treating an empty string as "untouched" would be wrong:
  // it would silently keep the first scan's name through a re-scan).
  const nameTouchedRef = useRef(false);
  const doseTouchedRef = useRef(false);

  // Reset whenever the modal re-opens so we don't carry stale form state.
  useEffect(() => {
    if (open) {
      setName('');
      setDose('');
      setSchedules([{ hour: 9, minute: 0 }]);
      setError(null);
      setScan(null);
      setScanThumbnail(null);
      nameTouchedRef.current = false;
      doseTouchedRef.current = false;
    }
  }, [open]);

  // Clean up the object URL when the thumbnail is replaced or the modal closes.
  // This is the *only* revoke site — doing it imperatively in handleScanFile
  // too would double-revoke (no-op per spec but Safari logs a warning).
  useEffect(() => {
    return () => {
      if (scanThumbnail) URL.revokeObjectURL(scanThumbnail);
    };
  }, [scanThumbnail]);

  const handleScanFile = async (file: File) => {
    if (scanningRef.current) return; // sync guard against rapid retap
    scanningRef.current = true;
    setError(null);
    setScanning(true);
    // Pre-render the chosen image immediately so the user gets feedback while
    // the vision call runs (typically 3-6s). The prior thumbnail is revoked
    // by the cleanup effect when scanThumbnail changes.
    setScanThumbnail(URL.createObjectURL(file));
    try {
      const result = await api.scanSupplement(file);
      setScan(result);
      // Only overwrite fields the user hasn't typed into. A re-scan should
      // still refresh fields the user hasn't touched.
      if (!nameTouchedRef.current) setName(result.name);
      if (!doseTouchedRef.current) setDose(result.dose);
    } catch (e) {
      setError(`Couldn't read that label: ${String(e)}`);
    } finally {
      scanningRef.current = false;
      setScanning(false);
    }
  };

  const submit = async () => {
    if (submitRef.current) return; // synchronous guard against double-click
    if (scanningRef.current) {
      setError('Wait for the scan to finish before saving');
      return;
    }
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (schedules.length === 0) {
      setError('Add at least one schedule time');
      return;
    }
    if (firstDuplicateTime(schedules) !== null) {
      setError('Duplicate schedule time — each row must be unique');
      return;
    }
    submitRef.current = true;
    setBusy(true);
    try {
      await api.addSupplement({
        name: name.trim(),
        dose: dose.trim(),
        info_label: scan?.info_label,
        coach_summary: scan?.coach_summary,
        tags: scan?.tags,
        scan_id: scan?.scan_id,
        schedules: schedules.map((s) => ({ hour: s.hour, minute: s.minute })),
      });
      onAdded();
    } catch (e) {
      setError(String(e));
    } finally {
      submitRef.current = false;
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Backdrop onClose={onClose}>
      <FormShell title="Add supplement" onClose={onClose}>
        <ScanBottleSection
          inputRef={scanInputRef}
          thumbnail={scanThumbnail}
          scan={scan}
          scanning={scanning}
          onPick={handleScanFile}
        />
        <Field
          label="Name"
          value={name}
          onChange={(v) => {
            nameTouchedRef.current = true;
            setName(v);
          }}
          maxLength={MAX_SUPPLEMENT_NAME}
          placeholder="Vitamin K2"
        />
        <Field
          label="Dose"
          value={dose}
          onChange={(v) => {
            doseTouchedRef.current = true;
            setDose(v);
          }}
          maxLength={MAX_SUPPLEMENT_DOSE}
          placeholder="100mcg / 5g / 400mg"
        />
        <ScheduleEditor schedules={schedules} setSchedules={setSchedules} />
        {error && <p role="alert" className="text-xs text-danger pt-1">{error}</p>}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={submit}
            disabled={busy || scanning}
            className="btn-primary flex-1 py-3 text-sm"
          >
            {busy ? 'Saving…' : scanning ? 'Scanning…' : 'Add'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex-1 py-3 text-sm"
          >
            Cancel
          </button>
        </div>
      </FormShell>
    </Backdrop>
  );
}

// ScanBottleSection — the "snap-a-photo" entry path. Renders a single button
// when nothing has been scanned; once a scan completes it shows the thumbnail
// + extracted label/tags so the user can verify the AI read it correctly
// before submitting.
function ScanBottleSection({
  inputRef,
  thumbnail,
  scan,
  scanning,
  onPick,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  thumbnail: string | null;
  scan: SupplementScan | null;
  scanning: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = ''; // allow re-picking the same file
        }}
      />
      {!thumbnail ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={scanning}
          className="btn-secondary w-full py-2.5 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-50"
        >
          {scanning ? 'Reading label…' : 'Scan bottle (photo + AI)'}
        </button>
      ) : (
        // A barely-there panel so the preview groups without stacking a second
        // grey box on the sheet; the photo itself is the sunken well.
        <div className="rounded-panel border border-line bg-bg-raised-2 p-2 space-y-2">
          <div className="flex items-start gap-3">
            <img
              src={thumbnail}
              alt="Scanned bottle"
              className="size-20 rounded-control object-cover bg-bg-sunken border border-line shrink-0"
            />
            <div className="flex-1 min-w-0 space-y-0.5">
              {scanning ? (
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                  Reading label…
                </p>
              ) : scan ? (
                <>
                  <p className="text-sm text-fg leading-snug">{scan.name || '— unreadable —'}</p>
                  {scan.dose && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                      {scan.dose}
                    </p>
                  )}
                  {scan.info_label && (
                    <p className="text-xs text-fg-muted leading-snug">{scan.info_label}</p>
                  )}
                </>
              ) : (
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                  No reading yet
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={scanning}
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint hover:text-fg transition-colors disabled:opacity-50"
          >
            Re-scan
          </button>
        </div>
      )}
    </div>
  );
}

// EditSupplementDrawer — same shape as AddSupplementModal but pre-fills the
// fields and PATCHes instead of POSTs.
export function EditSupplementDrawer({
  open,
  supplement,
  onClose,
  onSaved,
}: {
  open: boolean;
  supplement: Supplement;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(supplement.name);
  const [dose, setDose] = useState(supplement.dose);
  const [schedules, setSchedules] = useState<ScheduleDraft[]>(
    supplement.schedules.map((s) => ({ hour: s.hour, minute: s.minute })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitRef = useRef(false);

  useEffect(() => {
    if (open) {
      setName(supplement.name);
      setDose(supplement.dose);
      setSchedules(supplement.schedules.map((s) => ({ hour: s.hour, minute: s.minute })));
      setError(null);
    }
  }, [open, supplement]);

  const submit = async () => {
    if (submitRef.current) return;
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (firstDuplicateTime(schedules) !== null) {
      setError('Duplicate schedule time — each row must be unique');
      return;
    }
    submitRef.current = true;
    setBusy(true);
    try {
      await api.updateSupplement(supplement.id, {
        name: name.trim(),
        dose: dose.trim(),
        schedules,
      });
      onSaved();
    } catch (e) {
      setError(String(e));
    } finally {
      submitRef.current = false;
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Backdrop onClose={onClose}>
      <FormShell title="Edit supplement" onClose={onClose}>
        <Field label="Name" value={name} onChange={setName} maxLength={MAX_SUPPLEMENT_NAME} />
        <Field label="Dose" value={dose} onChange={setDose} maxLength={MAX_SUPPLEMENT_DOSE} />
        <ScheduleEditor schedules={schedules} setSchedules={setSchedules} />
        {error && <p role="alert" className="text-xs text-danger pt-1">{error}</p>}
        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="btn-primary flex-1 py-3 text-sm"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex-1 py-3 text-sm"
          >
            Cancel
          </button>
        </div>
      </FormShell>
    </Backdrop>
  );
}

// firstDuplicateTime returns the index of the first duplicate (hour,minute)
// slot found in the array, or null if all slots are unique. Used as a fast
// pre-submit check so we don't round-trip a guaranteed 400 to the server.
export function firstDuplicateTime(s: ScheduleDraft[]): number | null {
  const seen = new Set<number>();
  for (let i = 0; i < s.length; i++) {
    const key = s[i].hour * 60 + s[i].minute;
    if (seen.has(key)) return i;
    seen.add(key);
  }
  return null;
}

// --- shared internals ---

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  // ESC closes the modal. Click outside also closes; click inside is stopped.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-30 bg-fg/25 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
        {children}
      </div>
    </div>
  );
}

function FormShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    // Same sheet recipe as Modal.tsx — one overlay language app-wide. The 22px
    // radius and the modal shadow both come from CSS (`.surface-raised` +
    // `.surface-modal`), so no rounded-* / shadow-* utility here.
    <div className="surface-raised surface-modal p-5 space-y-3 max-h-[85vh] overflow-y-auto">
      <header className="flex items-baseline justify-between sticky top-0 bg-bg-raised -m-5 mb-0 px-5 pt-5 pb-3 z-10">
        <h2 className="font-display text-2xl">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg transition-colors"
        >
          Close
        </button>
      </header>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="label-eyebrow">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      />
    </label>
  );
}

// ScheduleEditor takes the parent's setter directly so all updates use
// functional setState. This avoids stale-closure bugs when the user clicks
// "+ Add time" twice in rapid succession before React has re-rendered.
function ScheduleEditor({
  schedules,
  setSchedules,
}: {
  schedules: ScheduleDraft[];
  setSchedules: Dispatch<SetStateAction<ScheduleDraft[]>>;
}) {
  const setAt = (i: number, patch: Partial<ScheduleDraft>) =>
    setSchedules((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) =>
    setSchedules((prev) => prev.filter((_, idx) => idx !== i));
  const add = () =>
    setSchedules((prev) =>
      prev.length >= MAX_SCHEDULES ? prev : [...prev, { hour: 12, minute: 0 }],
    );
  const dupIdx = firstDuplicateTime(schedules);
  return (
    <div className="space-y-1.5">
      <span className="label-eyebrow">Times</span>
      {schedules.map((s, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <select
            value={s.hour}
            onChange={(e) => setAt(i, { hour: Number(e.target.value) })}
            aria-label="Hour"
            className={`rounded-control bg-bg-sunken border px-2.5 py-2 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:bg-bg-raised ${
              i === dupIdx ? 'border-danger' : 'border-line focus:border-accent'
            }`}
          >
            {Array.from({ length: 24 }).map((_, h) => (
              <option key={h} value={h}>
                {pad(h)}
              </option>
            ))}
          </select>
          <span className="font-mono text-fg-faint text-sm">:</span>
          <select
            value={s.minute}
            onChange={(e) => setAt(i, { minute: Number(e.target.value) })}
            aria-label="Minute"
            className={`rounded-control bg-bg-sunken border px-2.5 py-2 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:bg-bg-raised ${
              i === dupIdx ? 'border-danger' : 'border-line focus:border-accent'
            }`}
          >
            {[0, 15, 30, 45].map((m) => (
              <option key={m} value={m}>
                {pad(m)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={schedules.length === 1}
            className="ml-auto grid place-items-center size-9 rounded-full text-fg-faint hover:text-danger hover:bg-danger-soft text-sm leading-none transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-faint"
            aria-label="Remove schedule"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={schedules.length >= MAX_SCHEDULES}
        className="btn-secondary w-full py-2.5 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-50"
      >
        + Add time
      </button>
    </div>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
